export interface VersionInfo {
	version: number;
	buildTime: string;
	timestamp: number;
}

export interface VersionCheckResult {
	currentVersion: number;
	latestVersion: number;
	updateAvailable: boolean;
	versionInfo?: VersionInfo;
}

export const fetchLatestVersion = async (): Promise<VersionInfo | null> => {
	try {
		const cacheBuster = Date.now();
		const response = await fetch(`/version.json?t=${cacheBuster}`, {
			method: 'GET',
			headers: {
				'Cache-Control': 'no-cache, no-store, must-revalidate',
				Pragma: 'no-cache',
			},
		});

		if (!response.ok) {
			console.error('Failed to fetch version:', response.statusText);
			return null;
		}

		const data: VersionInfo = await response.json();
		return data;
	} catch (error) {
		console.error('Error fetching latest version:', error);
		return null;
	}
};

export const getCurrentVersion = (): number => {
	const stored = sessionStorage.getItem('app-initial-version');
	if (stored) {
		const version = parseFloat(stored);
		if (!isNaN(version)) {
			return version;
		}
	}
	return 0;
};

export const setCurrentVersion = (version: number): void => {
	sessionStorage.setItem('app-initial-version', version.toString());
};

export const initializeVersion = async (): Promise<void> => {
	try {
		const versionInfo = await fetchLatestVersion();
		if (versionInfo) {
			setCurrentVersion(versionInfo.version);
		}
	} catch (error) {
		console.error('Failed to initialize version:', error);
	}
};

export const checkForUpdate = async (): Promise<VersionCheckResult | null> => {
	try {
		const currentVersion = getCurrentVersion();
		if (currentVersion === 0) {
			// First load: we don't yet know which JS bundle version is running.
			// We fetch the latest version and treat that as the current baseline.
			const latestVersionInfo = await fetchLatestVersion();
			if (!latestVersionInfo) {
				return null;
			}

			setCurrentVersion(latestVersionInfo.version);

			return {
				currentVersion: latestVersionInfo.version,
				latestVersion: latestVersionInfo.version,
				updateAvailable: false,
				versionInfo: undefined,
			};
		}

		const latestVersionInfo = await fetchLatestVersion();
		if (!latestVersionInfo) {
			return null;
		}

		const latestVersion = latestVersionInfo.version;
		const updateAvailable = latestVersion > currentVersion;

		return {
			currentVersion,
			latestVersion,
			updateAvailable,
			versionInfo: updateAvailable ? latestVersionInfo : undefined,
		};
	} catch (error) {
		console.error('Error checking for updates:', error);
		return null;
	}
};

export const reloadForUpdate = (): void => {
	sessionStorage.removeItem('app-initial-version');
	if ('caches' in window) {
		caches
			.keys()
			.then((names) => {
				names.forEach((name) => {
					caches.delete(name).catch(() => {
						// Ignore cache deletion errors; reloading will still pick up latest assets.
					});
				});
			})
			.catch(() => {
				// Ignore cache enumeration errors.
			});
	}

	window.location.reload();
};
